import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Avatar,
  TextField,
  InputAdornment,
  Grid,
  Divider,
  Badge,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  Instagram as InstagramIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const IG_PINK = '#E4405F';

const OUTBOUND_DISABLED_MSG =
  'Manual replies from the inbox are not available yet. Auto-replies and Send test work for whitelisted accounts.';

const AUTO_REPLY_ACTIVE_MSG =
  'Auto-replies are active for whitelisted test accounts. Use Send test on the page card to trigger a reply.';

const renderMessageBody = (msg) => {
  const type = msg.message_type || 'text';
  const url = msg.media_url;
  const attachments = msg.attachments || [];

  if (type === 'image' && url) {
    return (
      <>
        <Box component="img" src={url} alt="Instagram" sx={{ maxWidth: '100%', borderRadius: 1, mb: 0.5 }} />
        {msg.message_text && msg.message_text !== '[Image]' && (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{msg.message_text}</Typography>
        )}
      </>
    );
  }
  if (type === 'video' && url) {
    return (
      <>
        <Box component="video" src={url} controls sx={{ maxWidth: '100%', borderRadius: 1, mb: 0.5 }} />
        {msg.message_text && !msg.message_text.startsWith('[') && (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{msg.message_text}</Typography>
        )}
      </>
    );
  }
  if (type === 'audio' && url) {
    return <Box component="audio" src={url} controls sx={{ width: '100%' }} />;
  }
  if (url && type !== 'text') {
    return (
      <>
        <Typography variant="body2" sx={{ mb: 0.5 }}>[{type}]</Typography>
        <Typography variant="caption" component="a" href={url} target="_blank" rel="noopener noreferrer">
          View attachment
        </Typography>
      </>
    );
  }
  if (attachments.length > 0 && !msg.message_text) {
    return (
      <Typography variant="body2">
        [{attachments.map((a) => a.type).join(', ')}]
      </Typography>
    );
  }
  return (
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
      {msg.message_text || `[${type}]`}
    </Typography>
  );
};

const Messages = ({
  connectedAccounts = [],
  outboundEnabled = false,
  autoReplyEnabled = false,
  pageId: fixedPageId,
  embedded = false,
  allowReply = true,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [pageFilter, setPageFilter] = useState(fixedPageId || '');
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesContainerRef = useRef(null);

  const canReply = allowReply && (outboundEnabled || autoReplyEnabled);
  const accentColor = embedded ? 'primary.main' : IG_PINK;
  const outboundBubbleBg = embedded ? 'rgba(0, 112, 80, 0.08)' : '#FDEDF3';
  const resolvePageId = () =>
    fixedPageId || pageFilter || selectedConversation?.page_id || connectedAccounts[0]?.pageId || '';

  useEffect(() => {
    if (fixedPageId) {
      setPageFilter(fixedPageId);
    }
  }, [fixedPageId]);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = pageFilter ? { page_id: pageFilter } : {};
      const response = await api.get('/lms/instagram/conversations/', { params });
      setConversations(response.data.conversations || []);
    } catch (err) {
      console.error('Error fetching Instagram conversations:', err);
      setError('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConversations();
    const interval = setInterval(fetchConversations, 30000);
    return () => clearInterval(interval);
  }, [pageFilter]);

  useEffect(() => {
    if (fixedPageId || connectedAccounts.length !== 1 || pageFilter) return;
    setPageFilter(connectedAccounts[0].pageId);
  }, [connectedAccounts, pageFilter, fixedPageId]);

  const fetchMessages = async (conversationId) => {
    try {
      setLoadingMessages(true);
      const response = await api.get(`/lms/instagram/conversations/${conversationId}/messages/`);
      setMessages(response.data.messages || []);
      setSelectedConversation(response.data.conversation || selectedConversation);
    } catch (err) {
      console.error('Error fetching Instagram messages:', err);
      setError('Failed to load messages');
    } finally {
      setLoadingMessages(false);
    }
  };

  useEffect(() => {
    if (messages.length && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSelectConversation = async (conversation) => {
    setSelectedConversation(conversation);
    setReplyText('');
    await fetchMessages(conversation.id);
    if (conversation.unread_count > 0) {
      try {
        await api.patch(`/lms/instagram/conversations/${conversation.id}/`, { unread_count: 0 });
        setConversations((prev) =>
          prev.map((c) => (c.id === conversation.id ? { ...c, unread_count: 0 } : c))
        );
      } catch (err) {
        console.error('Error marking conversation read:', err);
      }
    }
  };

  const filtered = conversations.filter(
    (c) =>
      c.participant_username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.participant_id?.includes(searchQuery) ||
      c.last_message_preview?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatTime = (iso) => {
    if (!iso) return '';
    const date = new Date(iso);
    const diffMins = Math.floor((Date.now() - date) / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  };

  const handleSendReply = async () => {
    if (!selectedConversation || !replyText.trim()) return;

    const pageId = resolvePageId();
    const recipientId = selectedConversation.participant_id;
    if (!pageId || !recipientId) {
      setError('Missing page or recipient for reply.');
      return;
    }

    try {
      setSending(true);
      setError(null);
      const payload = {
        page_id: pageId,
        recipient_id: recipientId,
        message: replyText.trim(),
      };

      try {
        await api.post('/lms/instagram/messages/send/', payload);
      } catch (sendErr) {
        if (sendErr.response?.status === 403 && autoReplyEnabled) {
          await api.post('/lms/instagram/messages/trigger-test/', payload);
        } else {
          throw sendErr;
        }
      }

      setReplyText('');
      await fetchMessages(selectedConversation.id);
      await fetchConversations();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleReplyKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendReply();
    }
  };

  const statusAlert = () => {
    if (outboundEnabled || embedded) return null;
    if (autoReplyEnabled) {
      return (
        <Alert severity="success" sx={{ mb: 2 }}>
          {AUTO_REPLY_ACTIVE_MSG}
        </Alert>
      );
    }
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        {OUTBOUND_DISABLED_MSG}
      </Alert>
    );
  };

  const containerHeight = embedded ? '520px' : 'calc(100vh - 220px)';

  return (
    <Box sx={{ p: embedded ? 0 : 2, height: embedded ? 'auto' : containerHeight }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {statusAlert()}

      {connectedAccounts.length > 1 && !fixedPageId && (
        <FormControl size="small" sx={{ mb: 2, minWidth: 220 }}>
          <InputLabel>Facebook Page</InputLabel>
          <Select
            value={pageFilter}
            label="Facebook Page"
            onChange={(e) => setPageFilter(e.target.value)}
          >
            <MenuItem value="">All Pages</MenuItem>
            {connectedAccounts.map((a) => (
              <MenuItem key={a.pageId} value={a.pageId}>
                {a.pageName} (@{a.instagramUsername})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Grid container spacing={2} sx={{ height: embedded ? containerHeight : 'calc(100% - 48px)' }}>
        <Grid item xs={12} md={4} sx={{ height: '100%' }}>
          <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Divider />
            {loading ? (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress />
              </Box>
            ) : filtered.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center', flex: 1 }}>
                <InstagramIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography color="text.secondary">No Instagram conversations yet</Typography>
              </Box>
            ) : (
              <List sx={{ overflow: 'auto', flex: 1 }}>
                {filtered.map((conv) => (
                  <ListItem
                    key={conv.id}
                    button
                    selected={selectedConversation?.id === conv.id}
                    onClick={() => handleSelectConversation(conv)}
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      borderLeft: selectedConversation?.id === conv.id
                        ? `3px solid`
                        : '3px solid transparent',
                      borderLeftColor: selectedConversation?.id === conv.id ? accentColor : 'transparent',
                    }}
                  >
                    <Badge badgeContent={conv.unread_count || 0} color="error" invisible={!conv.unread_count}>
                      <Avatar sx={{ bgcolor: accentColor, mr: 1.5 }}>
                        {(conv.participant_username || '?')[0]?.toUpperCase()}
                      </Avatar>
                    </Badge>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" fontWeight={600}>
                          @{conv.participant_username || conv.participant_id}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {conv.last_message_preview || 'No messages'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatTime(conv.last_message_at)}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={8} sx={{ height: '100%' }}>
          <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {!selectedConversation ? (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">Select a conversation</Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    @{selectedConversation.participant_username || selectedConversation.participant_id}
                  </Typography>
                  {selectedConversation.instagram_username && (
                    <Typography variant="caption" color="text.secondary">
                      via @{selectedConversation.instagram_username}
                      {selectedConversation.page_name ? ` · ${selectedConversation.page_name}` : ''}
                    </Typography>
                  )}
                </Box>

                {loadingMessages ? (
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box ref={messagesContainerRef} sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                    {messages.map((msg) => (
                      <Box
                        key={msg.id}
                        sx={{
                          display: 'flex',
                          justifyContent: msg.direction === 'outbound' ? 'flex-end' : 'flex-start',
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            maxWidth: '70%',
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: msg.direction === 'outbound' ? outboundBubbleBg : 'grey.100',
                          }}
                        >
                          {renderMessageBody(msg)}
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {new Date(msg.message_timestamp).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                            {msg.message_type && msg.message_type !== 'text' ? ` · ${msg.message_type}` : ''}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}

                {canReply && (
                  <Box
                    sx={{
                      p: 1.5,
                      borderTop: 1,
                      borderColor: 'divider',
                      display: 'flex',
                      gap: 1,
                      alignItems: 'flex-end',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      maxRows={3}
                      placeholder="Type a reply…"
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      onKeyDown={handleReplyKeyDown}
                      disabled={sending}
                    />
                    <Button
                      variant="contained"
                      color={embedded ? 'primary' : 'inherit'}
                      disabled={sending || !replyText.trim()}
                      onClick={handleSendReply}
                      startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                      sx={embedded ? undefined : {
                        bgcolor: IG_PINK,
                        '&:hover': { bgcolor: '#c13584' },
                      }}
                    >
                      Reply
                    </Button>
                  </Box>
                )}

                {!canReply && !embedded && (
                  <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Alert severity="info">{OUTBOUND_DISABLED_MSG}</Alert>
                  </Box>
                )}
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Messages;
